import { useCallback } from "react";
import { AppId, FCX, usePlatformContext } from "@datassential/platform-ui-lib";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import useCategoryProfileBrandPerformance, {
    CategoryProfileBrandPerformanceData,
} from "@apiHooks/useCategoryProfileBrandPerformance";
import { ID } from "@models";
import { requestTrialByFeature } from "@actions/page";
import CategoryProfileSectionTitle from "@pages/CategoryProfile/components/CategoryProfileSectionTitle/CategoryProfileSectionTitle";
import CategoryProfileCard from "@pages/CategoryProfile/components/CategoryProfileCard/CategoryProfileCard";
import AccessLink from "@components/AccessLink/AccessLink";
import { CategoryProfileSectionComponentProps } from "@pages/CategoryProfile/CategoryProfile.constants";
import TopPerformingChains from "./components/TopPerformingChains/TopPerformingChains";
import { useParams } from "react-router-dom";
import http from "@core/fetch";
import apiConfig from "@apiConfig";

export const useBrandPerformanceSectionData = (categoryId: ID) => {
    const { data, isLoading } = useCategoryProfileBrandPerformance(categoryId);
    const hasData = data.chains.length > 0;
    return { hasData, isLoading, data };
};

const CategoryProfileBrandPerformance: FCX<CategoryProfileSectionComponentProps> = ({ title, data }) => {
    const { id: categoryId } = useParams<{ id: string }>();
    const sectionData = data as CategoryProfileBrandPerformanceData;

    const dispatch = useDispatch();
    const { userInfo: { products } } = usePlatformContext();
    const brandPerformance = products[AppId.BrandPerformance];

    const requestTrial = useCallback(() => {
        toast.info("Request has been sent");
        dispatch(requestTrialByFeature("brandPerformances"));
    }, [dispatch]);

    const handleDownload = useCallback(
        () => http.downloadFileXHRFromUrl(
            'GET',
            `${apiConfig['get category profile brands report']}?categoryProfileId=${categoryId}`,
        ),
        [categoryId]
    );

    return (
        <div className="CategoryProfile__section-content CategoryProfileBrandPerformance">
            <CategoryProfileSectionTitle
                title={title}
                onDownload={handleDownload}
            />

            <CategoryProfileCard>
                <TopPerformingChains
                    chains={sectionData.chains}
                    categoryNames={sectionData.categories}
                    bpUrl={brandPerformance?.url ?? ""} />
            </CategoryProfileCard>

            <AccessLink
                url={brandPerformance?.url || null}
                text="More Brand Performance"
                altText="Get the full Datassential One experience!"
                hasAccess={!brandPerformance?.isDisabled}
                onContactUsClick={requestTrial}
                testId="linkCategoryProfileMoreBrandPerformance"
            />
        </div>
    );
};

export default CategoryProfileBrandPerformance;
